/**
 * Primitives shared by every schema module in this package. Pulled out of `index.ts` so a new
 * schema module (e.g. `practice.ts`) can depend on them without creating an import cycle back
 * into `index.ts` (which itself re-exports every other module, `practice.ts` included).
 */
import { z } from 'zod';

const HANZI_RE = /^[㐀-鿿]$/u;
const HANZI_STRING_RE = /^[㐀-鿿]+$/u;

export const Hanzi = z.string().regex(HANZI_RE, 'expected exactly one CJK character');
export const HanziString = z.string().regex(HANZI_STRING_RE, 'expected CJK characters only');
export const Pinyin = z.string().min(1);
export const Id = z.string().regex(/^[a-z0-9][a-z0-9\-_.]*$/, 'ids are lowercase kebab/dot');
/** Media keys are free-form (e.g. `audio:char:鱼`), resolved through LessonBundle.media. */
export const MediaId = z.string().min(1);

export const EditionId = z.enum(['2024', '2015']);
export type EditionId = z.infer<typeof EditionId>;

export const LevelId = z.enum(['p1', 'p2', 'p3', 'p4', 'p5', 'p6']);
export type LevelId = z.infer<typeof LevelId>;

export const Semester = z.enum(['a', 'b']);

export const Skill = z.enum(['recognise', 'write']);
export type Skill = z.infer<typeof Skill>;

export const LocalisedText = z.object({ zh: z.string().min(1), en: z.string().min(1) });
export type LocalisedText = z.infer<typeof LocalisedText>;

/** One token of a sentence-shaped thing: a run of hanzi (or punctuation), its pinyin when it has
 * any, and the word it belongs to (for pinyin-on-tap / tap-to-hear lookups). Shared by `Sentence`
 * and, at passage scope, `PassageSentence` (`practice.ts`) — a passage is "an ordered list of
 * Sentence-shaped things", not a new token type. */
export const SentenceToken = z.object({
  text: z.string().min(1),
  pinyin: Pinyin.nullable(),
  wordId: Id.nullable(),
});
export type SentenceToken = z.infer<typeof SentenceToken>;
